/**
 * Turning push on, keeping it on, and turning it off at sign-out.
 *
 * Three entry points, one shared body of work. `enablePush` is the only one that may show the
 * browser's permission dialog; `syncPushToken` runs on every start for accounts that already said
 * yes; `forgetPushToken` runs at sign-out.
 *
 * **Nothing here throws at its callers.** Push is an enhancement layered on an app that works
 * fully without it. A push service that is unreachable, a corporate policy that blocks the
 * subscription, a Firebase outage — all of them must produce a banner that stays put, never a
 * screen that fails to render or a sign-out that appears to hang.
 */
import { registerDeviceToken, WEB_PLATFORM } from '@/entities/device-token/api';
import { obtainFcmToken, revokeFcmToken } from './firebase';
import { currentPushAvailability, type PushAvailability } from './support';

/**
 * What the last successful registration sent, as `<token>|<consumer id>`.
 *
 * The account id is half of the key on purpose. Tokens are per *browser*, not per person: when
 * two people use the same phone, the second one signs in holding a token the backend still has
 * filed under the first. Keying on the token alone would skip the POST, and every notification
 * meant for the second person would keep going to a row owned by the first.
 */
const LAST_REGISTERED_KEY = 'tt:push-token';

export type PushOutcome =
  | { status: 'registered' }
  /** Already registered under this account; nothing was sent. */
  | { status: 'unchanged' }
  | { status: 'denied' }
  | { status: 'needs-install' }
  | { status: 'unsupported' }
  /** Something went wrong that is worth retrying — network, push service, Firebase. */
  | { status: 'failed'; error: unknown };

function readLastRegistered(): string | null {
  try {
    return localStorage.getItem(LAST_REGISTERED_KEY);
  } catch {
    // Storage can throw outright in a locked-down Safari. Treat it as "nothing remembered": the
    // cost is one redundant POST per load, which the backend answers with registered: false.
    return null;
  }
}

function rememberRegistered(value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(LAST_REGISTERED_KEY);
    else localStorage.setItem(LAST_REGISTERED_KEY, value);
  } catch {
    // Same as above — losing the memo costs a redundant POST, not correctness.
  }
}

function outcomeFor(availability: Exclude<PushAvailability, 'ready' | 'granted'>): PushOutcome {
  return { status: availability };
}

/**
 * Fetches the token and hands it to the backend, skipping the call when nothing changed.
 *
 * Waits on `navigator.serviceWorker.ready` rather than registering anything: the worker is put up
 * by `swUpdate` at start, and a token bound to a registration that later gets replaced is a token
 * that quietly stops receiving.
 */
async function registerCurrentToken(consumerId: string): Promise<PushOutcome> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const token = await obtainFcmToken(registration);
    const memo = `${token}|${consumerId}`;
    if (readLastRegistered() === memo) return { status: 'unchanged' };

    await registerDeviceToken(token, WEB_PLATFORM);
    rememberRegistered(memo);
    return { status: 'registered' };
  } catch (error) {
    return { status: 'failed', error };
  }
}

/**
 * Asks for permission if it has not been asked for yet, then registers.
 *
 * Only ever call this from a real tap. `Notification.requestPermission()` fired on load is both a
 * dark pattern and self-defeating: Chrome degrades the dialog to a muted bell for sites that do
 * it, and a person who has not yet seen what the app is for reflexively says no — which cannot be
 * undone from JS, ever.
 */
export async function enablePush(consumerId: string): Promise<PushOutcome> {
  const availability = currentPushAvailability();
  if (availability !== 'ready' && availability !== 'granted') return outcomeFor(availability);

  if (availability === 'ready') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { status: 'denied' };
  }

  return registerCurrentToken(consumerId);
}

/**
 * Keeps an already-granted account registered, without ever prompting.
 *
 * Runs on every start because an FCM token is not permanent: it rotates when the browser's push
 * subscription is recreated, which happens on its own after a storage eviction or a long absence.
 * Without this, push works for a while after the person opts in and then silently stops, with
 * nothing on either side to indicate it.
 */
export async function syncPushToken(consumerId: string): Promise<PushOutcome> {
  if (currentPushAvailability() !== 'granted') return { status: 'unchanged' };
  return registerCurrentToken(consumerId);
}

/**
 * Sign-out: stop this browser receiving pushes meant for the account that just left.
 *
 * Best-effort by design, and the memo is cleared first. If the revoke fails, the next sign-in
 * still re-registers (the memo is gone), so the worst case is a stale row the backend prunes on
 * its next send — never a person who keeps getting somebody else's notifications.
 */
export async function forgetPushToken(): Promise<void> {
  rememberRegistered(null);
  if (currentPushAvailability() !== 'granted') return;
  try {
    await revokeFcmToken();
  } catch {
    // See above: the backend's UNREGISTERED handling is the backstop.
  }
}
